import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { CollectError, CollectDivergence } from '@/hooks/useCollectsMonitoring';

interface CollectsMonitoringTableProps {
  type: 'errors' | 'problems' | 'coalesce';
  data: CollectError[] | CollectDivergence[];
}

const DIVERGENCE_LABELS: Record<string, string> = {
  'STATUS': 'Status divergente',
  'WEIGHT': 'Peso divergente',
  'VOLUME': 'Volume divergente',
  'UNITS': 'Unidades divergentes',
  'COALESCE_WEIGHT': 'COALESCE protegeu peso',
  'COALESCE_VOLUME': 'COALESCE protegeu volume',
  'COALESCE_UNITS': 'COALESCE protegeu unidades'
};

export const CollectsMonitoringTable = ({ type, data }: CollectsMonitoringTableProps) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <Info className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg ">Nenhum registro encontrado</p>
        <p className="text-sm text-gray-400 mt-1">
          {type === 'errors' && 'Sem erros de processamento na última semana'}
          {type === 'problems' && 'Sem divergências problemáticas detectadas'}
          {type === 'coalesce' && 'Sem divergências de COALESCE detectadas'}
        </p>
      </div>
    );
  }

  if (type === 'errors') {
    const errors = data as CollectError[];
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">ID Coleta</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Data Planejada</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Estabelecimento</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Material</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Motivo do Erro</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Data do Erro</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((error) => (
              <tr key={error.collect_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4 font-mono text-xs text-gray-600">
                  {error.collect_id.substring(0, 8)}...
                </td>
                <td className="py-3 px-4  text-sm">
                  {format(new Date(error.planned_date), 'dd/MM/yyyy', { locale: ptBR })}
                </td>
                <td className="py-3 px-4  text-sm text-gray-700">
                  {error.establishment_name || '-'}
                </td>
                <td className="py-3 px-4  text-sm">
                  {error.kind_of_material || '-'}
                </td>
                <td className="py-3 px-4">
                  <Badge variant="destructive" className=" text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {error.skip_reason}
                  </Badge>
                </td>
                <td className="py-3 px-4  text-sm text-gray-600">
                  {format(new Date(error.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Tabelas de divergências (problems e coalesce)
  const divergences = data as CollectDivergence[];
  const isProblems = type === 'problems';

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b-2 border-gray-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">ID Coleta</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Data Planejada</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Estabelecimento</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Material</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Tipo de Divergência</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Valor Atual</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">Valor Esperado</th>
          </tr>
        </thead>
        <tbody>
          {divergences.map((div) => {
            const currentValue = div.divergence_type === 'STATUS' || div.divergence_type === 'STATUS_NULL' 
              ? div.current_status 
              : div.divergence_type.includes('WEIGHT') 
                ? div.current_weight 
                : div.divergence_type.includes('VOLUME')
                  ? div.current_volume
                  : div.current_units;

            const expectedValue = div.divergence_type === 'STATUS' || div.divergence_type === 'STATUS_NULL'
              ? div.expected_status
              : div.divergence_type.includes('WEIGHT')
                ? div.expected_weight
                : div.divergence_type.includes('VOLUME')
                  ? div.expected_volume
                  : div.expected_units;

            return (
              <tr key={div.collect_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4 font-mono text-xs text-gray-600">
                  {div.collect_id.substring(0, 8)}...
                </td>
                <td className="py-3 px-4  text-sm">
                  {format(new Date(div.planned_date), 'dd/MM/yyyy', { locale: ptBR })}
                </td>
                <td className="py-3 px-4  text-sm text-gray-700">
                  {div.establishment_name || '-'}
                </td>
                <td className="py-3 px-4  text-sm">
                  {div.kind_of_material || '-'}
                </td>
                <td className="py-3 px-4">
                  <Badge 
                    variant={isProblems ? "default" : "secondary"} 
                    className=" text-xs"
                  >
                    {isProblems ? (
                      <AlertTriangle className="h-3 w-3 mr-1" />
                    ) : (
                      <Info className="h-3 w-3 mr-1" />
                    )}
                    {DIVERGENCE_LABELS[div.divergence_type] || div.divergence_type}
                  </Badge>
                </td>
                <td className="py-3 px-4  text-sm font-medium">
                  {currentValue !== null && currentValue !== undefined ? String(currentValue) : '-'}
                </td>
                <td className="py-3 px-4  text-sm text-gray-600">
                  {expectedValue !== null && expectedValue !== undefined ? String(expectedValue) : 'NULL'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
